import { apiUrl } from './apiUrl';

const COOKIE_CONSENT_STORAGE_KEY = 'jumptakeCookieConsent';
const VISITOR_STORAGE_KEY = 'jumptakeAnalyticsVisitor';
const ADMIN_PANEL_PATH = process.env.REACT_APP_ADMIN_PANEL_PATH || '/jt-owner-console-9x4k2-admin';

const hasAnalyticsConsent = () => {
  try {
    return localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === 'allowed';
  } catch (error) {
    return false;
  }
};

const createVisitorId = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `visitor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const getVisitorId = () => {
  if (!hasAnalyticsConsent()) return '';
  try {
    const existing = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (existing) return existing;
    const visitorId = createVisitorId();
    localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
    return visitorId;
  } catch (error) {
    return '';
  }
};

const cleanValue = (value = '', maxLength = 180) => String(value || '')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, maxLength);

const normalizePath = (value = '') => {
  const path = cleanValue(value, 300).split(/[?#]/)[0];
  return path.startsWith('/') ? path : '/';
};

const sendAnalyticsEvent = (event) => {
  if (!hasAnalyticsConsent()) return;
  const path = normalizePath(event.path || window.location.pathname);
  if (path.startsWith('/jt-owner-console-') || path.startsWith(ADMIN_PANEL_PATH)) return;

  fetch(apiUrl('/api/analytics/events'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      eventType: event.eventType,
      path,
      target: cleanValue(event.target),
      label: cleanValue(event.label),
      sessionId: getVisitorId()
    }),
    keepalive: true
  }).catch(() => {
    // Product navigation must never depend on analytics delivery.
  });
};

export const trackPageView = (path) => {
  sendAnalyticsEvent({ eventType: 'page_view', path });
};

const getClickDescriptor = (element) => {
  const href = element.tagName === 'A' ? element.getAttribute('href') : '';
  let hrefPath = '';
  if (href) {
    try {
      hrefPath = new URL(href, window.location.origin).pathname;
    } catch (error) {
      hrefPath = '';
    }
  }

  const classTarget = String(element.className || '').split(/\s+/).find(Boolean) || '';
  return {
    label: element.dataset.analyticsLabel
      || element.getAttribute('aria-label')
      || element.getAttribute('title')
      || element.value
      || element.textContent,
    target: element.dataset.analyticsId || element.id || hrefPath || classTarget || element.tagName.toLowerCase()
  };
};

export const installClickAnalytics = () => {
  const handleClick = (event) => {
    const element = event.target?.closest?.('button, a, [role="button"], input[type="submit"]');
    if (!element || element.closest('.admin-panel')) return;
    const descriptor = getClickDescriptor(element);
    sendAnalyticsEvent({
      eventType: 'click',
      path: window.location.pathname,
      ...descriptor
    });
  };

  document.addEventListener('click', handleClick, true);
  return () => document.removeEventListener('click', handleClick, true);
};

export { hasAnalyticsConsent, normalizePath };
